// Org member-management data layer (#308). Roster + invite / change-role /
// remove over `apiFetch`, reusing the session bearer. Account-scoped on the
// server via the active tenant (the `X-Dz-Account` header `activeAccountId`
// feeds), so the query key includes it — switching orgs re-scopes the roster.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';

const ROLES: readonly OrgRole[] = ['owner', 'admin', 'member', 'viewer'];
const RANK: Record<OrgRole, number> = { owner: 3, admin: 2, member: 1, viewer: 0 };

/** Client mirror of `canManageMember`'s authority rule — owner ▸ anyone, else strictly-below. */
export const canManage = (actor: OrgRole, target: OrgRole): boolean =>
  actor === 'owner' || RANK[actor] > RANK[target];
/** Roles the actor may grant — owner ▸ all, else strictly below itself. */
export const grantable = (actor: OrgRole): readonly OrgRole[] =>
  ROLES.filter((r) => (actor === 'owner' ? true : RANK[r] < RANK[actor]));
/** Whether an actor role can manage anyone at all (owner/admin). */
export const canManageAny = (actor: OrgRole): boolean => actor === 'owner' || actor === 'admin';

/** `GET /v1/org/members` projection — see `apps/api/src/routes/org-members.ts`. */
export type OrgMemberRow = {
  readonly user_id: string;
  readonly login: string;
  readonly avatar: string | null;
  readonly email: string | null;
  readonly role: OrgRole;
  readonly joined_iso: string;
  /** True for the signed-in caller — the UI guards self-removal / self-demotion. */
  readonly is_self: boolean;
};

export const ORG_MEMBERS_KEY = 'org_members';

/**
 * The org roster. `enabled` is an ACCESSOR, not a value: the only other caller is
 * a panel that stays mounted while shut (`<MachineActions/>`'s Who it works for),
 * so a gate read once at setup would latch on whatever the panel's state was at
 * mount and never open — `useBoxDetail(boxId, …)` takes its id the same way and for the
 * same reason.
 *
 * The wire is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null: the route serves the WHOLE roster, so there is no page two to
 * reach and no Load-more to offer.
 */
export function useOrgMembers(
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<readonly OrgMemberRow[]>(() => ({
    queryKey: [ORG_MEMBERS_KEY, token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () => (await apiFetch<Page<OrgMemberRow>>('/v1/org/members', token)).data,
  }));
}

/**
 * How an invite ADDRESSES its target — the client mirror of the route's
 * login/email XOR (`apps/api/src/routes/org-members.ts:55-69`). A discriminated
 * union rather than two optional fields: the server refuses a body naming both
 * or neither instead of resolving it by precedence, so the shape that would
 * silently decide who joins the org is not expressible here either.
 */
export type InviteTarget =
  | { readonly by: 'login'; readonly login: string }
  | { readonly by: 'email'; readonly email: string };

/**
 * The `invitation` an email invite minted — `MemberInvitationView`
 * (`packages/mcp/src/army/member.ts:94`), on the wire as the 202 body.
 *
 * `accept_url` carries a single-use bearer token and is answered HERE and
 * nowhere else: the row keeps only its sha256 and `listInvitations`
 * structurally cannot project either column. So it is rendered once, at mint,
 * and never routed to, never a query key, never cached — the same stance
 * `ArtifactLinkPanel` takes on a presigned URL.
 */
export type OrgInvitationMint = {
  readonly invitation_id: string;
  readonly email: string;
  readonly role: OrgRole;
  readonly accept_url: string;
  readonly expires_at_iso: string;
  /** true = an existing live invitation was re-armed — its previous link is dead. */
  readonly reissued: boolean;
  /** Whether the invitation mail LEFT; false = the link below is the only way in (03-13). */
  readonly mailed: boolean;
};

/**
 * Which of the route's TWO success shapes came back. `member` means the person
 * is on the roster right now; `invited` means they are not and a link went out.
 * Collapsing them would let this UI report a teammate as added when nothing is
 * yet true of the org (`apps/api/src/routes/org-members.ts:118-122`).
 *
 * Discriminated on the BODY the server sent, not on the channel we asked with:
 * `apiFetch` does not surface the status, and the answer we render should be
 * the one the server gave rather than the one we predicted.
 */
export type OrgInviteResult =
  | { readonly outcome: 'member'; readonly member: OrgMemberRow }
  | { readonly outcome: 'invited'; readonly invitation: OrgInvitationMint };

/**
 * Invite at a role, by GitHub login (an already-registered user) or by email
 * (anyone — a one-time link is minted and mailed).
 *
 * **This is also the resend.** `createInvitation` is idempotent by rotation: a
 * second email invite for an address that already has a live invitation re-arms
 * that row with a fresh token and a fresh expiry rather than inserting a second
 * one (`packages/db/src/queries/invitations.ts:154-162`), and answers
 * `reissued: true`. So resending is this call with the same `{email, role}` —
 * there is no second endpoint to add, and adding one would be a second way to
 * mint the same object.
 */
export async function inviteMember(
  target: InviteTarget,
  role: OrgRole,
  token: string | null = SESSION_TOKEN,
): Promise<OrgInviteResult> {
  const body =
    target.by === 'login' ? { login: target.login, role } : { email: target.email, role };
  // why the cast: an unvalidated wire body. `invitation` is the discriminator —
  // a roster row has no such field, and an invited outcome has nothing else.
  const answer = await apiFetch<OrgMemberRow | { readonly invitation: OrgInvitationMint }>(
    '/v1/org/members',
    token,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return 'invitation' in answer
    ? { outcome: 'invited', invitation: answer.invitation }
    : { outcome: 'member', member: answer };
}

/** Change a member's role. */
export async function changeMemberRole(
  userId: string,
  role: OrgRole,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch(`/v1/org/members/${encodeURIComponent(userId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

/** Remove a member from the org. */
export async function removeMember(
  userId: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch(`/v1/org/members/${encodeURIComponent(userId)}`, token, { method: 'DELETE' });
}

/**
 * `GET /v1/org/members/invitations` projection. Answers EVERY status
 * (`pending`/`accepted`/`expired`/`revoked`) newest-first — the pending list
 * (`<PendingInvitations/>`) filters to `pending` client-side, the same way the
 * roster answers members and a caller narrows. Structurally carries neither a
 * token nor an `accept_url`, the same reason `OrgInvitationMint` documents:
 * `listInvitations` never projects either column, so there is nothing here to
 * leak.
 */
export type OrgInvitationRow = {
  readonly id: string;
  readonly kind: 'org_member';
  readonly account_id: string | null;
  readonly role: OrgRole | null;
  readonly email: string;
  readonly status: 'pending' | 'accepted' | 'expired' | 'revoked';
  readonly invited_by_user_id: string | null;
  readonly expires_at_iso: string;
  readonly created_at_iso: string;
  readonly accepted_at_iso: string | null;
  readonly revoked_at_iso: string | null;
};

export const ORG_INVITATIONS_KEY = 'org_invitations';

/**
 * Every invitation this org has ever minted (`useOrgMembers`'s shape, mirrored
 * exactly — same `Page` unwrap, same `next_cursor: null` "this IS the whole
 * collection" contract, same account-scoped key).
 */
export function useOrgInvitations(
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<readonly OrgInvitationRow[]>(() => ({
    queryKey: [ORG_INVITATIONS_KEY, token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () =>
      (await apiFetch<Page<OrgInvitationRow>>('/v1/org/members/invitations', token)).data,
  }));
}

/**
 * Revoke a pending invitation. Idempotent — revoking an already-revoked row
 * still 204s, mirroring `removeMember`'s idempotence and the admin-grants
 * "revoked:false is reported by re-reading" doctrine: the caller re-reads
 * (`useOrgInvitations().refetch()`) rather than patching from this call's
 * empty body. Refuses 404 (unknown id, or another org's — folded into ONE
 * not-found) and 409 (already accepted — you cannot revoke someone who
 * already joined).
 */
export async function revokeInvitation(
  invitationId: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch(`/v1/org/members/invitations/${encodeURIComponent(invitationId)}`, token, {
    method: 'DELETE',
  });
}
